import { useDispatch, useSelector } from 'react-redux';
import { track } from '@amplitude/analytics-browser';
import { GreenroomState } from '../stores/types/types';
import Socket from '../connection/socket';
import {
	CreateTeleprompter,
	EditTeleprompter,
	ShareTeleprompter,
	CreateTeleprompterSection,
	EditTeleprompterSection,
	GetTeleprompterSharedWith,
} from '../connection/teleprompters';
import * as TeleprompterActions from '../stores/actions/teleprompters';
import { TeleprompterInterface, TeleprompterSectionInterface } from '../types/interfaces';
import { AmpEvent } from '../types/amplitude';
import { MediaItem } from '../stores/reducers/media';

const useTeleprompters = (socket: Socket | null) => {
	const dispatch = useDispatch();
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const session = useSelector((state: GreenroomState) => state.SessionReducer.session);
	const teleprompters = useSelector(
		(state: GreenroomState) => state.TeleprompterReducer.teleprompters,
	);

	const syncTeleprompters = async (
		teleprompter: TeleprompterInterface,
		syncSession: boolean = true,
	) => {
		if (!token || !meeting || !user || !socket) {
			return;
		}

		const sharedWith = await GetTeleprompterSharedWith(token, teleprompter.id);
		const creator = teleprompter.gr_user;

		socket.sendMessage('teleprompter-updated', {
			meeting: meeting.meeting,
			id: teleprompter.id,
			users: teleprompter.media_id ? null : [creator, ...sharedWith],
			from: syncSession ? null : session,
		});
	};

	const loadTeleprompters = () => {
		if (token && user?.gr_user && meeting?.meeting) {
			dispatch(TeleprompterActions.loadTeleprompters(token, user?.gr_user, meeting?.meeting));
		}
	};

	const createTeleprompter = async (
		teleprompter: TeleprompterInterface,
	): Promise<TeleprompterInterface | null> => {
		if (!token) {
			return null;
		}

		const createdTeleprompter = await CreateTeleprompter(token, teleprompter);

		if (createdTeleprompter) {
			track(AmpEvent.shotNoteCreated);
			syncTeleprompters(createdTeleprompter);
			return createdTeleprompter;
		}

		return null;
	};

	const editTeleprompter = async (teleprompter: TeleprompterInterface) => {
		if (!token) {
			return;
		}

		if (await EditTeleprompter(token, teleprompter)) {
			track(AmpEvent.shotNoteEdited);
			syncTeleprompters(teleprompter);
		}
	};

	const shareTeleprompter = async (teleprompterId: number, shareWithIds: number[]) => {
		const teleprompter = teleprompters.find((t) => t.id === teleprompterId);
		if (!token || !teleprompter) {
			return;
		}

		if (await ShareTeleprompter(token, teleprompterId, shareWithIds)) {
			syncTeleprompters(teleprompter);
		}
	};

	const createTeleprompterSections = async (
		teleprompterId: number,
		teleprompterSection: TeleprompterSectionInterface,
	): Promise<TeleprompterSectionInterface | null> => {
		const teleprompter = teleprompters.find((t) => t.id === teleprompterId);
		if (!token || !teleprompter) {
			return null;
		}

		const createdTeleprompterSection = await CreateTeleprompterSection(
			token,
			teleprompterId,
			teleprompterSection,
		);

		if (createdTeleprompterSection) {
			syncTeleprompters(teleprompter, false);
			return createdTeleprompterSection;
		}

		return null;
	};

	const editTeleprompterSections = async (
		teleprompterId: number,
		teleprompterSection: TeleprompterSectionInterface,
	) => {
		const teleprompter = teleprompters.find((t) => t.id === teleprompterId);
		if (!token || !teleprompter) {
			return;
		}

		if (await EditTeleprompterSection(token, teleprompterId, teleprompterSection)) {
			track(AmpEvent.shotNoteEdited);
			syncTeleprompters(teleprompter, false);
		}
	};

	const handleSetMediaTeleprompter = async (mediaItem: MediaItem) => {
		let mediaTeleprompter = teleprompters.find(
			(tprompter) => tprompter.media_id === mediaItem.id,
		) ?? null;

		if (!mediaTeleprompter && token && meeting && user) {
			mediaTeleprompter = await createTeleprompter({
				id: 0,
				meeting: meeting?.meeting,
				gr_user: mediaItem.created_by,
				media_id: mediaItem.id,
				sections: [],
				created_date: '',
				shared_with: [],
			});
		}

		if (mediaTeleprompter) {
			dispatch(TeleprompterActions.setSelectedTeleprompters(['media', mediaTeleprompter.id]));
		}
	};

	return {
		loadTeleprompters,
		createTeleprompter,
		editTeleprompter,
		createTeleprompterSections,
		editTeleprompterSections,
		shareTeleprompter,
		handleSetMediaTeleprompter,
	};
};

export default useTeleprompters;
